import { serverContext } from "js/config";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from "./resources";

const LOCALE_STORAGE_KEY = "bai-locale";
const LOCALE_COOKIE_NAME = "locale";

function isSupportedLocale(locale?: string | null): locale is SupportedLocale {
    return !!locale && SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

function readStoredLocale() {
    try {
        if (typeof window !== "undefined" && window.localStorage) {
            return window.localStorage.getItem(LOCALE_STORAGE_KEY);
        }
    } catch (err) {
        console.warn("[i18n] failed to read stored locale", err);
    }
    return null;
}

function writeStoredLocale(locale: SupportedLocale) {
    try {
        if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
        }
    } catch (err) {
        console.warn("[i18n] failed to store locale preference", err);
    }
}

function setLocaleCookie(locale: SupportedLocale) {
    if (typeof document === "undefined") {
        return;
    }

    const maxAge = 60 * 60 * 24 * 365; // one year
    document.cookie = `${LOCALE_COOKIE_NAME}=${locale}; path=/; max-age=${maxAge}`;
    document.documentElement?.setAttribute("lang", locale);
}

export function getInitialLocale(): SupportedLocale {
    const stored = readStoredLocale();
    if (isSupportedLocale(stored)) {
        return stored;
    }

    if (isSupportedLocale(serverContext.locale)) {
        return serverContext.locale;
    }

    return DEFAULT_LOCALE;
}

export function getAvailableLocales(): SupportedLocale[] {
    const fromServer = serverContext.availableLocales;
    if (Array.isArray(fromServer) && fromServer.length > 0) {
        return fromServer.filter(isSupportedLocale) as SupportedLocale[];
    }
    return SUPPORTED_LOCALES;
}

export function setPreferredLocale(locale: SupportedLocale) {
    if (!isSupportedLocale(locale)) {
        return;
    }

    serverContext.locale = locale;
    serverContext.availableLocales = getAvailableLocales();
    writeStoredLocale(locale);
    setLocaleCookie(locale);
}
